import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useNotification } from '../contexts/NotificationContext'
import { Eye, EyeOff, Check, X, Loader2, AlertTriangle, UserPlus, Lock, Building2, KeyRound } from 'lucide-react'
import api from '../services/api'

/**
 * AcceptInvitePage - Pagina per accettare invito e impostare password
 * 
 * URL: /accept-invite?token=xxxxx
 * 
 * AGGIORNATO: Supporto PIN di verifica
 * FIX: Corretto salvataggio token in localStorage (era 'accessToken', ora 'token')
 */
export default function AcceptInvitePage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { login } = useAuth()
  const { showSuccess, showError } = useNotification()

  const token = searchParams.get('token')

  // Stati
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [inviteData, setInviteData] = useState(null)
  const [error, setError] = useState(null)
  
  // Form
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [pin, setPin] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  
  // PIN error
  const [pinError, setPinError] = useState(null)

  // Validazione password
  const passwordChecks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[^A-Za-z0-9]/.test(password),
    match: password === confirmPassword && password.length > 0
  }

  const isPasswordValid = Object.values(passwordChecks).every(Boolean)
  
  // Validazione PIN (se richiesto)
  const isPinValid = !inviteData?.requiresPin || (pin.length === 6 && /^\d{6}$/.test(pin))

  // Verifica token all'avvio
  useEffect(() => {
    if (!token) {
      setError('Link non valido. Manca il token di invito.')
      setLoading(false)
      return
    }

    verifyInvite()
  }, [token])

  const verifyInvite = async () => {
    try {
      setLoading(true)
      const response = await api.get(`/auth/verify-invite?token=${token}`)
      
      if (response.data.success) {
        setInviteData(response.data.data)
      } else {
        setError(response.data.message || 'Invito non valido')
      }
    } catch (err) {
      console.error('Verify invite error:', err)
      const message = err.response?.data?.message || 'Errore nella verifica dell\'invito'
      
      if (err.response?.data?.code === 'INVALID_TOKEN') {
        setError('Questo invito non è valido o è già stato utilizzato.')
      } else if (err.response?.data?.code === 'EXPIRED_TOKEN') {
        setError('Questo invito è scaduto. Richiedi un nuovo invito al coordinatore.')
      } else {
        setError(message)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setPinError(null)

    if (!isPasswordValid) {
      showError('Verifica i requisiti della password')
      return
    }
    
    if (inviteData?.requiresPin && !isPinValid) {
      showError('Inserisci il PIN di 6 cifre')
      return
    }

    try {
      setSubmitting(true)
      
      const payload = {
        token,
        password
      }
      
      // Aggiungi PIN se richiesto
      if (inviteData?.requiresPin) {
        payload.pin = pin
      }
      
      const response = await api.post('/auth/accept-invite', payload)

      if (response.data.success) {
        // Cookie httpOnly impostati dal backend nella risposta
        const { user } = response.data.data

        showSuccess(`Benvenuto ${user.nome}! Il tuo account è stato attivato.`)

        // Redirect alla dashboard — checkAuth() caricherà lo state dal cookie
        setTimeout(() => {
          window.location.href = '/dashboard'
        }, 500)
      }
    } catch (err) {
      console.error('Accept invite error:', err)
      const message = err.response?.data?.message || 'Errore durante l\'attivazione'
      const code = err.response?.data?.code
      
      // Gestione errori PIN
      if (code === 'INVALID_PIN') {
        const remaining = err.response?.data?.attemptsRemaining
        setPinError(`PIN non valido. ${remaining > 0 ? `${remaining} tentativi rimanenti.` : ''}`)
        setPin('') // Reset PIN
      } else if (code === 'PIN_BLOCKED') {
        setError('Troppi tentativi PIN errati. Contatta il responsabile per un nuovo invito.')
      } else if (code === 'PIN_REQUIRED') {
        setPinError('Inserisci il PIN che ti è stato comunicato dal responsabile.')
      } else if (code === 'EXPIRED_TOKEN') {
        setError('L\'invito è scaduto. Richiedi un nuovo invito al coordinatore.')
      } else {
        showError(message)
      }
    } finally {
      setSubmitting(false)
    }
  }

  // Loading
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 dark:from-gray-900 dark:to-gray-800">
        <div className="text-center">
          <Loader2 className="h-10 w-10 text-primary-600 animate-spin mx-auto" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Verifica invito in corso...</p>
        </div>
      </div>
    )
  }

  // Error
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 dark:from-gray-900 dark:to-gray-800 p-4">
        <div className="w-full max-w-md">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="h-8 w-8 text-red-600 dark:text-red-400" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Invito non valido
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {error}
            </p>
            <Link
              to="/login"
              className="inline-flex items-center justify-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Torna al Login
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 dark:from-gray-900 dark:to-gray-800 p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-primary-600 dark:text-primary-400">
            📘 Il Diario di Bordo
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Gestione centri educativi
          </p>
        </div>

        {/* Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <UserPlus className="h-8 w-8 text-primary-600 dark:text-primary-400" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Benvenuto, {inviteData?.nome}!
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Sei stato invitato come <span className="font-semibold text-primary-600 dark:text-primary-400">{inviteData?.role}</span>
            </p>
          </div>

          {/* Centro info */}
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3">
              <Building2 className="h-5 w-5 text-gray-500" />
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Centro educativo</p>
                <p className="font-semibold text-gray-900 dark:text-white">{inviteData?.centroName}</p>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* PIN (se richiesto) */}
            {inviteData?.requiresPin && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <div className="flex items-center gap-2">
                    <KeyRound className="h-4 w-4" />
                    PIN di verifica
                  </div>
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={pin}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '')
                    setPin(value)
                    setPinError(null)
                  }}
                  placeholder="Inserisci il PIN a 6 cifre"
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-center text-2xl tracking-widest ${
                    pinError ? 'border-red-500' : ''
                  }`}
                />
                {pinError && (
                  <p className="mt-1 text-sm text-red-500">{pinError}</p>
                )}
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Il PIN ti è stato comunicato dal coordinatore
                </p>
              </div>
            )}

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <div className="flex items-center gap-2">
                  <Lock className="h-4 w-4" />
                  Nuova Password
                </div>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Crea una password sicura"
                  className="w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Conferma Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Conferma Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Ripeti la password"
                  className="w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Password requirements */}
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-2">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Requisiti password:
              </p>
              {[
                { key: 'length', label: 'Almeno 8 caratteri' },
                { key: 'uppercase', label: 'Almeno una lettera maiuscola' },
                { key: 'number', label: 'Almeno un numero' },
                { key: 'special', label: 'Almeno un carattere speciale' },
                { key: 'match', label: 'Le password coincidono' }
              ].map(({ key, label }) => (
                <div key={key} className="flex items-center gap-2 text-sm">
                  {passwordChecks[key] ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <X className="h-4 w-4 text-gray-400" />
                  )}
                  <span className={passwordChecks[key] ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}>
                    {label}
                  </span>
                </div>
              ))}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={!isPasswordValid || !isPinValid || submitting}
              className="w-full py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Attivazione in corso...
                </>
              ) : (
                <>
                  <UserPlus className="h-5 w-5" />
                  Attiva Account
                </>
              )}
            </button>
          </form>

          {/* Footer */}
          <p className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
            Hai già un account?{' '}
            <Link to="/login" className="text-primary-600 hover:text-primary-700 font-semibold">
              Accedi
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
